import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RyderCupTeam } from 'src/app/_interfaces';
import { GolfTournament } from 'src/app/_interfaces/golf/golf-tournament';
import { AppStringsService } from 'src/app/_services';

import { RYDER_CUP_TEAMS, RyderCupTeamKey } from 'src/app/_constants/ryder-cup-team.constants';
import { GolfEventStatus, TeamScore } from 'src/app/_interfaces/golf/gol-match-card';
import { AppStringComponent } from '../../app-string/app-string.component';


type PrimaryTeamEvent = {
    id: string;
    team_score?: TeamScore
    status?: GolfEventStatus;
};

@Component({
    selector: 'app-golf-team-score-card',
    imports: [CommonModule, AppStringComponent],
    templateUrl: './golf-team-score-card.component.html',
    styleUrls: ['./golf-team-score-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GolfTeamScoreCardComponent implements OnChanges {
    @Input({ required: true }) golfTour!: GolfTournament;

    left = { team: null as RyderCupTeam | null, score: '', winner: false, projected: null as string | null, projectedText: '' };
    right = { team: null as RyderCupTeam | null, score: '', winner: false, projected: null as string | null, projectedText: '' };
    hasPrimary = false;
    status: GolfEventStatus = null

    private readonly TEAM1_RED = 'var(--mp-team1-red, #ef4444)';
    private readonly TEAM2_BLUE = 'var(--mp-team2-blue, #3b82f6)';

    constructor(private strings: AppStringsService) { }

    ngOnChanges(_: SimpleChanges): void {
        const ev = this.golfTour?.events?.find(
            (e) => e?.type === 'match_play' && e?.is_primary_team_event === true
        ) as PrimaryTeamEvent | undefined;

        this.hasPrimary = !!ev?.team_score;
        this.status = ev?.status ?? null;

        if (!this.hasPrimary) {
            this.left = { team: null, score: '', winner: false, projected: null, projectedText: '' };
            this.right = { team: null, score: '', winner: false, projected: null, projectedText: '' };
            return;
        }

        const toKey = (n?: string | null): RyderCupTeamKey | null => {
            const v = (n || '').toLowerCase();
            if (v === 'europe') return RyderCupTeamKey.EUROPE;
            if (v === 'usa') return RyderCupTeamKey.USA;
            return null;
        };

        const t1 = ev!.team_score!.team_1 ?? {};
        const t2 = ev!.team_score!.team_2 ?? {};

        const ryderMode = this.isRyderCupLike();

        if (ryderMode) {
            const leftKey = toKey(t1.name);
            const rightKey = toKey(t2.name);

            const showProjected = this.status === 'inprogress';

            const fmtProjected = (value: string | null): { val: string | null, text: string } => {
                if (!showProjected || !value) return { val: null, text: '' };
                const tmpl = this.strings.getAppString('golf.matchplay.team.projectedscore') || '%@ Projected22';
                return { val: value, text: tmpl.replace('%@', String(value)) };
            };

            const leftProj = fmtProjected(t1.projected_score ?? null);
            const rightProj = fmtProjected(t2.projected_score ?? null);

            this.left = {
                team: leftKey ? RYDER_CUP_TEAMS[leftKey] : null,
                score: t1.mp_score ?? (this.hasPrimary && this.golfTour.events.length > 1 ? '0' : ''),
                winner: t1.mp_result === 'won',
                projected: leftProj.val,
                projectedText: leftProj.text
            };

            this.right = {
                team: rightKey ? RYDER_CUP_TEAMS[rightKey] : null,
                score: t2.mp_score ?? (this.hasPrimary && this.golfTour.events.length > 1 ? '0' : ''),
                winner: t2.mp_result === 'won',
                projected: rightProj.val,
                projectedText: rightProj.text
            };
        } else {
            // Standard Match Play (team_1 / team_2 → Red / Blue)
            // Build “team-like” objects compatible with RyderCupTeam shape.
            const team1: RyderCupTeam = {
                name: t1.name || 'Team 1',
                code: null,
                color: this.TEAM1_RED
            } as unknown as RyderCupTeam;

            const team2: RyderCupTeam = {
                name: t2.name || 'Team 2',
                code: null,
                color: this.TEAM2_BLUE
            } as unknown as RyderCupTeam;

            const showProjected = this.status === 'inprogress';
            const fmtProjected = (value: string | null): { val: string | null, text: string } => {
                if (!showProjected || !value) return { val: null, text: '' };
                const tmpl = this.strings.getAppString('golf.matchplay.team.projectedscore') || '%@ Projected';
                return { val: value, text: tmpl.replace('%@', String(value)) };
            };

            const leftProj = fmtProjected(t1.projected_score ?? null);
            const rightProj = fmtProjected(t2.projected_score ?? null);

            this.left = {
                team: team1,
                score: t1.mp_score ?? (this.hasPrimary && this.golfTour.events.length > 1 ? '0' : ''),
                winner: t1.mp_result === 'won',
                projected: leftProj.val,
                projectedText: leftProj.text
            };

            this.right = {
                team: team2,
                score: t2.mp_score ?? (this.hasPrimary && this.golfTour.events.length > 1 ? '0' : ''),
                winner: t2.mp_result === 'won',
                projected: rightProj.val,
                projectedText: rightProj.text
            };
        }
    }

    tTeam(team: RyderCupTeam | null): string {
        if (!team) return '';
        return this.strings.getAppString(team.name) ?? team.name;
    }

    private isRyderCupLike(): boolean {
        const ev = this.golfTour?.events ?? [];
        return ev.some(e => {
            const n1 = e?.team_score?.team_1?.name?.toLowerCase?.();
            const n2 = e?.team_score?.team_2?.name?.toLowerCase?.();
            return n1 === 'europe' || n1 === 'usa' || n2 === 'europe' || n2 === 'usa';
        });
    }
}
